import { useTOptions, VALIDATION_ERROR_KEYS } from "@/i18n"
import { DataDrivenFormSchema } from "@/lib/data-driven-forms/schema"
import * as z from "zod"

const boolean = z.enum(["true", "false"], VALIDATION_ERROR_KEYS.REQUIRED)

const optionalString = z.string().optional().nullable()
const requiredString = z
  .string(VALIDATION_ERROR_KEYS.REQUIRED)
  .min(1, VALIDATION_ERROR_KEYS.REQUIRED)

const addressDetailsSchema = z.object({
  formatted_address: z.string().optional().nullable(),
  place_id: z.string().optional().nullable(),
  lat: z.number().optional().nullable(),
  lng: z.number().optional().nullable(),
})

export const schoolProfileZodSchema = z.object({
  school: z.object({
    school_name: requiredString,
    address_details: addressDetailsSchema.nullable(),
    contact_email: z.email(VALIDATION_ERROR_KEYS.MUST_BE_VALID_EMAIL),
    contact_phone_number: z.e164(VALIDATION_ERROR_KEYS.MUST_BE_VALID_PHONE),
    school_url: z.url(VALIDATION_ERROR_KEYS.MUST_BE_VALID_URL).nullable(),
    platform_technology_google_meet: boolean,
    platform_technology_microsoft_teams: boolean,
    platform_technology_zoom: boolean,
    platform_technology_other: boolean,
    platform_technology_other_description: optionalString,
  }),
})

export const formSchema: DataDrivenFormSchema<"school_profiles"> = {
  type: "form",
  namespace: "school_profiles",
  validator: schoolProfileZodSchema,
  properties: [
    {
      type: "section:hash",
      sectionKey: "school_information",
      title: "form.subsections.school_information",
      properties: [
        {
          type: "section:scrolling",
          sectionKey: "general_information",
          title: "form.school_information.subsections.general_information",
          properties: [
            {
              type: "field:input",
              inputType: "text",
              name: "school.school_name",
              label: "form.school_information.general_information.school_name",
            },
            {
              type: "field:place",
              name: "school.address_details",
              label:
                "form.school_information.general_information.school_address",
            },
            {
              type: "field:input",
              name: "school.contact_email",
              label:
                "form.school_information.general_information.contact_email",
              inputType: "email",
            },
            {
              type: "field:input",
              name: "school.contact_phone_number",
              label:
                "form.school_information.general_information.contact_number",
              inputType: "tel",
            },
            {
              type: "field:input",
              name: "school.school_url",
              label: "form.school_information.general_information.school_url",
              inputType: "url",
            },
          ],
        },
        {
          type: "section:scrolling",
          sectionKey: "school_platforms_used",
          title: "form.school_information.subsections.school_platforms_used",
          properties: [
            {
              type: "field:radio",
              name: "school.platform_technology_google_meet",
              label:
                "form.school_information.school_platforms_used.platform_technology_google_meet",
              useOptions: () => useTOptions("boolean", "common"),
            },
            {
              type: "field:radio",
              name: "school.platform_technology_microsoft_teams",
              label:
                "form.school_information.school_platforms_used.platform_technology_microsoft_teams",
              useOptions: () => useTOptions("boolean", "common"),
            },
            {
              type: "field:radio",
              name: "school.platform_technology_zoom",
              label:
                "form.school_information.school_platforms_used.platform_technology_zoom",
              useOptions: () => useTOptions("boolean", "common"),
            },
            {
              type: "field:radio",
              name: "school.platform_technology_other",
              label:
                "form.school_information.school_platforms_used.platform_technology_other",
              useOptions: () => useTOptions("boolean", "common"),
            },
            {
              type: "field:input",
              inputType: "text",
              name: "school.platform_technology_other_description",
              label:
                "form.school_information.school_platforms_used.platform_technology_other_description",
            },
          ],
        },
      ],
    },
  ],
}
